/**
 * Home body matching version9/4 · Home-html (content only).
 * Layout milestone = 3 setup steps done - not Publish (§30).
 * Live Activity = 6 tiles incl. Chose another country / Stayed or closed (§34).
 * Title bar actions + confirm modals live on HomePage.
 * Phases: draft (not published) · test · live.
 */
import { useEffect, useMemo, useState } from "react";
import { AppButton } from "./AppButton";
import { countryLabel, formatNumber } from "../lib/format";

export type HomePhase = "draft" | "test" | "live";

/** Mock Preview countries - always these five, same order as Home.dc.html */
export const HOME_PREVIEW_COUNTRIES = ["CA", "GB", "DE", "BR", "US"] as const;

type SetupStep = {
  id: string;
  title: string;
  done: boolean;
  btn: string;
  how?: string;
  onAct: () => void;
};

type Props = {
  phase: HomePhase;
  shopLabel: string;
  marketCount: number;
  localesHint?: string;
  setupSteps: SetupStep[];
  setupDoneCount: number;
  setupReady: boolean;
  redirectsUsed: number;
  redirectCap: number | null | undefined;
  monthLabel: string;
  resetsLabel: string;
  testPreviews: number;
  testWouldPopup: number;
  testPopups: number;
  /** Silent auto redirects in preview (`is_test=true`). Does not burn plan quota. */
  testAuto: number;
  liveVisitors: number;
  liveEligible: number;
  livePopups: number;
  liveAuto: number;
  /** §34: popup Continue to another country (of Popups shown). */
  liveChoseAnother: number;
  /** §34: X / same-market Continue (of Popups shown). */
  liveStayedOrClosed: number;
  explainPreview: (cc: string) => { result: string; why: string[] };
  onRefreshShopify: () => void;
  /** Controlled Preview country - also drives title-bar Open store in test. */
  previewCc: string;
  onPreviewCcChange: (cc: string) => void;
};

export function HomeV6Body({
  phase,
  shopLabel,
  marketCount,
  localesHint,
  setupSteps,
  setupDoneCount,
  setupReady,
  redirectsUsed,
  redirectCap,
  monthLabel,
  resetsLabel,
  testPreviews,
  testWouldPopup,
  testPopups,
  testAuto,
  liveVisitors,
  liveEligible,
  livePopups,
  liveAuto,
  liveChoseAnother,
  liveStayedOrClosed,
  explainPreview,
  onRefreshShopify,
  previewCc,
  onPreviewCcChange,
}: Props) {
  /** Activity tab: Test when not live phase; Live when live. */
  const [act, setAct] = useState<"test" | "live">(
    phase === "live" ? "live" : "test",
  );
  const [openStep, setOpenStep] = useState<string | null>(
    () => setupSteps.find((s) => !s.done)?.id ?? null,
  );
  /** After 3/3: collapsed “Setup complete” until merchant expands. */
  const [stepsOpen, setStepsOpen] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [quotaDismissed, setQuotaDismissed] = useState(() => {
    try {
      return localStorage.getItem("sg_quota_warn_dismissed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    setAct(phase === "live" ? "live" : "test");
  }, [phase]);

  const published = phase !== "draft";
  const isLive = phase === "live";
  const isDraft = phase === "draft";

  // v8 §30: milestone = setupReady, not publish.
  const showSetup = isDraft && (!setupReady || stepsOpen);
  const showDone = isDraft && setupReady && !stepsOpen;
  /** Activity once 3 steps done (Test-only) or after Publish (Test/Live tabs). */
  const showAct = published || setupReady;
  const showActTabs = published;

  const statTitle = isLive
    ? "SafeGeo is live"
    : published
      ? "SafeGeo is in Test mode"
      : setupReady
        ? "Setup is complete. Ready to publish"
        : "SafeGeo is not published yet";
  const statText = isLive
    ? "Visitors from countries with a rule are asked, redirected or blocked. Changes you save apply right away."
    : published
      ? "Visitors are not affected. Switch to Live to start serving them. Changes you save apply automatically."
      : setupReady
        ? "Visitors are not affected. Only you see the popup, when you open your store in test. Publish when you are ready."
        : "Visitors are not affected. Only you see the popup, when you open your store in test. Publish once when setup is done.";

  const cap = redirectCap ?? null;
  const pct =
    cap != null && cap > 0
      ? Math.min(100, Math.round((redirectsUsed / cap) * 100))
      : 0;
  const quotaWarn =
    isLive && cap != null && pct >= 80 && pct < 100 && !quotaDismissed;
  const quotaFull = isLive && cap != null && redirectsUsed >= cap;

  const testTiles = useMemo(
    () => [
      { label: "Previews you ran", val: formatNumber(testPreviews) },
      {
        label: "Would get a popup or redirect",
        val: formatNumber(testWouldPopup),
      },
      { label: "Popups shown to you", val: formatNumber(testPopups) },
      { label: "Automatic redirects", val: formatNumber(testAuto) },
    ],
    [testPreviews, testWouldPopup, testPopups, testAuto],
  );

  const liveTiles = useMemo(() => {
    const popups = livePopups;
    const pctOfPopups = (n: number) => {
      if (popups <= 0) return `${formatNumber(n)} · —`;
      const pct = Math.round((n / popups) * 100);
      return `${formatNumber(n)} · ${pct}%`;
    };
    return [
      { label: "Visitors checked", val: formatNumber(liveVisitors) },
      { label: "Eligible to redirect", val: formatNumber(liveEligible) },
      { label: "Popups shown", val: formatNumber(livePopups) },
      { label: "Automatic redirects", val: formatNumber(liveAuto) },
      {
        label: "Chose another country",
        val: pctOfPopups(liveChoseAnother),
      },
      {
        label: "Stayed or closed",
        val: pctOfPopups(liveStayedOrClosed),
      },
    ];
  }, [
    liveVisitors,
    liveEligible,
    livePopups,
    liveAuto,
    liveChoseAnother,
    liveStayedOrClosed,
  ]);

  /** Pre-publish Activity is Test-only (v8). */
  const showActTest = !published || act === "test";
  const showActLiveEmpty = published && act === "live" && !isLive;
  const showActLive = published && act === "live" && isLive;

  const explained = explainPreview(previewCc);

  return (
    <s-stack gap="base">
      {/* —— Status card (actions are on s-page title bar) —— */}
      <s-section>
        <s-box padding="base" borderWidth="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading>{statTitle}</s-heading>
            <s-paragraph color="subdued">{statText}</s-paragraph>

            {isDraft && !setupReady ? (
              <s-paragraph color="subdued">
                Finish steps 1 to 3 below to publish.
              </s-paragraph>
            ) : null}

            {showDone ? (
              <>
                <s-divider />
                <s-stack
                  direction="inline"
                  gap="base"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-badge tone="success">✓</s-badge>
                    <s-text type="strong">Setup complete</s-text>
                  </s-stack>
                  <s-button
                    type="button"
                    variant="tertiary"
                    onClick={() => setStepsOpen(true)}
                  >
                    Show steps
                  </s-button>
                </s-stack>
              </>
            ) : null}

            {showSetup ? (
              <s-stack gap="base">
                <s-divider />
                <s-stack
                  direction="inline"
                  gap="base"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <s-heading>
                    {setupReady ? "Setup steps" : "Finish setup"}
                  </s-heading>
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-text color="subdued">{setupDoneCount} of 3 done</s-text>
                    {setupReady ? (
                      <s-button
                        type="button"
                        variant="tertiary"
                        onClick={() => setStepsOpen(false)}
                      >
                        Hide steps
                      </s-button>
                    ) : null}
                  </s-stack>
                </s-stack>
                <s-progress
                  value={setupDoneCount}
                  max={3}
                  accessibilityLabel={`${setupDoneCount} of 3 setup steps done`}
                />
                <s-box padding="base" borderRadius="base" background="subdued">
                  <s-paragraph color="subdued">
                    <s-text type="strong">
                      We read your store from Shopify:
                    </s-text>{" "}
                    {shopLabel}
                    {marketCount
                      ? ` · ${marketCount} market${marketCount === 1 ? "" : "s"}`
                      : ""}
                    {localesHint ? ` · ${localesHint}` : ""}.{" "}
                    <s-button
                      type="button"
                      variant="tertiary"
                      onClick={onRefreshShopify}
                    >
                      Refresh
                    </s-button>
                  </s-paragraph>
                </s-box>
                {setupSteps.map((step, i) => {
                  const open = openStep === step.id && !!step.how;
                  return (
                    <s-stack key={step.id} gap="small">
                      {i > 0 ? <s-divider /> : null}
                      <s-stack
                        direction="inline"
                        gap="base"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <s-stack
                          direction="inline"
                          gap="small"
                          alignItems="center"
                        >
                          <s-badge tone={step.done ? "success" : "info"}>
                            {step.done ? "✓" : String(i + 1)}
                          </s-badge>
                          <s-text type="strong">{step.title}</s-text>
                        </s-stack>
                        <s-button
                          type="button"
                          variant={step.done ? "tertiary" : "secondary"}
                          onClick={() => {
                            if (step.how) {
                              setOpenStep(open ? null : step.id);
                            }
                            step.onAct();
                          }}
                        >
                          {step.done ? "Done" : step.btn}
                        </s-button>
                      </s-stack>
                      {open ? (
                        <s-box
                          padding="base"
                          borderRadius="base"
                          background="subdued"
                        >
                          <s-paragraph color="subdued">{step.how}</s-paragraph>
                        </s-box>
                      ) : null}
                    </s-stack>
                  );
                })}
              </s-stack>
            ) : null}
          </s-stack>
        </s-box>
      </s-section>

      {quotaWarn && cap != null ? (
        <s-banner
          tone="warning"
          heading={`You’ve used ${pct}% of this month’s automatic redirects.`}
        >
          At {formatNumber(cap)}, automatic rules ask first. Nothing is blocked
          and visitors can still choose.
          <s-stack direction="inline" gap="small">
            <s-button
              type="button"
              onClick={() => {
                try {
                  localStorage.setItem("sg_quota_warn_dismissed", "1");
                } catch {
                  /* ok */
                }
                setQuotaDismissed(true);
              }}
            >
              Dismiss
            </s-button>
            <AppButton variant="primary" to="/app/billing">
              See plans
            </AppButton>
          </s-stack>
        </s-banner>
      ) : null}
      {quotaFull && cap != null ? (
        <s-banner
          tone="critical"
          heading={`You’ve used all ${formatNumber(cap)} automatic redirects for ${monthLabel}.`}
        >
          Automatic rules now ask first, so visitors can still choose. Nothing
          is blocked. The count resets {resetsLabel}.{" "}
          <AppButton variant="primary" to="/app/billing">
            See plans
          </AppButton>
        </s-banner>
      ) : null}

      {/* —— Activity (v8: after 3 steps OR after Publish) —— */}
      {showAct ? (
        <s-section heading="Activity">
          <s-stack gap="base">
            {showActTabs ? (
              <s-stack direction="inline" gap="small">
                <s-button
                  type="button"
                  variant={act === "test" ? "primary" : "secondary"}
                  onClick={() => setAct("test")}
                >
                  Test
                </s-button>
                <s-button
                  type="button"
                  variant={act === "live" ? "primary" : "secondary"}
                  onClick={() => setAct("live")}
                >
                  Live
                </s-button>
              </s-stack>
            ) : null}

            {showActTest ? (
              <s-stack gap="base">
                <s-paragraph color="subdued">
                  Only your own test activity. Starts at 0 and grows as you try
                  countries below. Not counted in live stats or in your monthly
                  limit.
                </s-paragraph>
                <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                  {testTiles.map((t) => (
                    <s-box
                      key={t.label}
                      padding="base"
                      borderWidth="base"
                      borderRadius="base"
                    >
                      <s-stack gap="small">
                        <s-text color="subdued">{t.label}</s-text>
                        <s-heading>{t.val}</s-heading>
                      </s-stack>
                    </s-box>
                  ))}
                </s-grid>
              </s-stack>
            ) : null}

            {showActLiveEmpty ? (
              <s-box padding="large">
                <s-stack gap="small" alignItems="center">
                  <s-text type="strong">No live visitors yet</s-text>
                  <s-paragraph color="subdued">
                    Live numbers appear once SafeGeo is live.
                  </s-paragraph>
                </s-stack>
              </s-box>
            ) : null}

            {showActLive ? (
              <s-stack gap="base">
                <s-stack
                  direction="inline"
                  gap="base"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-heading>Automatic redirects · {monthLabel}</s-heading>
                    <s-badge tone="success">● Live · updated just now</s-badge>
                  </s-stack>
                  <s-text color="subdued">Resets {resetsLabel}</s-text>
                </s-stack>
                <s-stack direction="inline" gap="small" alignItems="baseline">
                  <s-heading>{formatNumber(redirectsUsed)}</s-heading>
                  <s-text color="subdued">
                    {cap != null
                      ? `of ${formatNumber(cap)} this month`
                      : "this month · Unlimited"}
                  </s-text>
                </s-stack>
                {cap != null ? (
                  <s-progress
                    value={redirectsUsed}
                    max={cap}
                    accessibilityLabel={`${pct}% of automatic redirects used`}
                  />
                ) : null}
                <s-paragraph color="subdued">
                  Only automatic redirects count. Choosing a country in the
                  popup is the visitor’s choice and is never counted toward the
                  limit.
                </s-paragraph>
                <s-paragraph color="subdued">
                  Last 7 days, real visitors.
                </s-paragraph>
                <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                  {liveTiles.map((t) => (
                    <s-box
                      key={t.label}
                      padding="base"
                      borderWidth="base"
                      borderRadius="base"
                    >
                      <s-stack gap="small">
                        <s-text color="subdued">{t.label}</s-text>
                        <s-heading>{t.val}</s-heading>
                      </s-stack>
                    </s-box>
                  ))}
                </s-grid>
              </s-stack>
            ) : null}
          </s-stack>
        </s-section>
      ) : null}

      {/* —— Preview as a visitor —— */}
      <s-section heading="Preview as a visitor">
        <s-stack gap="base">
          <s-paragraph color="subdued">
            Pick a country to see exactly what that visitor would get.
          </s-paragraph>
          <s-stack direction="inline" gap="small">
            {HOME_PREVIEW_COUNTRIES.map((code) => (
              <s-button
                key={code}
                type="button"
                variant={previewCc === code ? "primary" : "secondary"}
                onClick={() => {
                  onPreviewCcChange(code);
                  setShowWhy(false);
                }}
              >
                {countryLabel(code)}
              </s-button>
            ))}
          </s-stack>
          <s-box padding="base" borderWidth="base" borderRadius="base">
            <s-paragraph color="subdued">{explained.result}</s-paragraph>
          </s-box>
          {testPreviews > 0 ? (
            <s-paragraph color="subdued">
              You tried {formatNumber(testPreviews)}{" "}
              {testPreviews === 1 ? "country" : "countries"}. Tests are not
              counted in live stats or in your monthly limit.
            </s-paragraph>
          ) : null}
          <s-button
            type="button"
            variant="tertiary"
            onClick={() => setShowWhy((v) => !v)}
          >
            {showWhy ? "Hide the steps" : "Why? Show the steps"}
          </s-button>
          {showWhy ? (
            <s-box padding="base" borderWidth="base" borderRadius="base">
              <s-ordered-list>
                {explained.why.map((line) => (
                  <s-list-item key={line}>{line}</s-list-item>
                ))}
              </s-ordered-list>
            </s-box>
          ) : null}
        </s-stack>
      </s-section>
    </s-stack>
  );
}
